import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useStateContext } from "../contexts/ContextProvider";
import { useCartContext } from "../contexts/CartContext";
import { isShopEnabled } from "../utility/featureFlags";

const ShopLink = ({className, onClick}) => (
    <Link to="/shop" onClick={onClick} className={`inline-flex items-center ${className}`}>
        Shop
        <span aria-hidden="true" className="ml-1 text-xs font-semibold text-pegasusPurple bg-pink-100 px-1.5 py-0.5 rounded-full">
            NEU
        </span>
    </Link>
);

const CartButton = ({cartCount, onClick}) => (
    <button onClick={onClick} className="relative hover:text-pegasusPurple text-gray-700" aria-label="Warenkorb">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        {cartCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-pegasusPurple text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {cartCount}
            </span>
        )}
    </button>
);

const Navbar = () => {
    const { user, token, setUser, setToken } = useStateContext();
    const { cartCount, openDrawer } = useCartContext();
    const [isNavbarActive, setIsNavbarActive] = useState(false);
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = location.pathname;

    const getActiveClass = (path) =>
        currentPath === path ? "text-pegasusPurple font-bold" : "hover:text-pegasusPurple text-gray-700";

    const handleLogout = () => {
        setUser(null);
        setToken(null);
        navigate("/");
    };

    const closeMenu = () => setIsNavbarActive(false);
    const closeAccountMenu = () => setDropdownVisible(false);

    return (
        <nav className="fixed top-0 left-0 w-full bg-white shadow-md z-50">
            <div className="container mx-auto flex justify-between items-center px-4 py-3">
                {/* Logo */}
                <Link to="/" onClick={closeMenu}>
                    <img src="/images/pegasus-pole-text.png" alt="Pegasus Pole" className="h-10" />
                </Link>

                {/* Mobile header: the shop stays outside the menu so it is seen without opening it */}
                <div className="flex items-center gap-4 md:hidden">
                    {isShopEnabled() && (
                        <>
                            <ShopLink className={getActiveClass("/shop")} onClick={closeMenu}/>
                            <CartButton
                                cartCount={cartCount}
                                onClick={() => {
                                    openDrawer();
                                    closeMenu();
                                }}
                            />
                        </>
                    )}
                    <button
                        onClick={() => setIsNavbarActive(!isNavbarActive)}
                        className="text-gray-600"
                        aria-label="Menü"
                        aria-expanded={isNavbarActive}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>
                </div>

                {/* Desktop Menu */}
                <ul className="hidden md:flex md:items-center md:space-x-6">
                    <li>
                        <a href={process.env.REACT_APP_BASE_SITE_URL} className="hover:text-pegasusPurple text-gray-700">
                            Studio
                        </a>
                    </li>
                    <li>
                        <Link to="/" className={getActiveClass("/")}>
                            Kursplan
                        </Link>
                    </li>
                    {isShopEnabled() && (
                        <>
                            <li>
                                <ShopLink className={getActiveClass("/shop")}/>
                            </li>
                            <li>
                                <CartButton cartCount={cartCount} onClick={openDrawer}/>
                            </li>
                        </>
                    )}
                    {token ? (
                        <li className="relative">
                            <button
                                onClick={() => setDropdownVisible(!dropdownVisible)}
                                className="bg-pegasusPurple text-white rounded-full w-8 h-8 flex items-center justify-center font-bold"
                            >
                                {user?.firstName?.[0]?.toUpperCase() || ""}
                                {user?.lastName?.[0]?.toUpperCase() || ""}
                            </button>
                            {dropdownVisible && (
                                <div className="absolute right-0 mt-2 bg-white shadow-md rounded-lg w-32 text-sm">
                                    <Link
                                        to="/account"
                                        onClick={closeAccountMenu}
                                        className="block px-4 py-2 hover:bg-gray-100 text-gray-700"
                                    >
                                        Account
                                    </Link>
                                    <button
                                        onClick={handleLogout}
                                        className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700"
                                    >
                                        Ausloggen
                                    </button>
                                </div>
                            )}
                        </li>
                    ) : (
                        <li>
                            <Link
                                to="/login"
                                className="text-pegasusPurple border border-pegasusPurple py-2 px-4 rounded hover:bg-pegasusPurple hover:text-white"
                            >
                                Einloggen
                            </Link>
                        </li>
                    )}
                </ul>

                {/* Mobile Menu */}
                <ul
                    className={`${
                        isNavbarActive ? "flex" : "hidden"
                    } md:hidden flex-col items-center absolute top-16 left-0 w-full bg-white py-8 space-y-6 text-center`}
                >
                    <li onClick={closeMenu}>
                        <a href={process.env.REACT_APP_BASE_SITE_URL} className="hover:text-pegasusPurple text-gray-700">
                            Studio
                        </a>
                    </li>
                    <li onClick={closeMenu}>
                        <Link to="/" className={getActiveClass("/")}>
                            Kursplan
                        </Link>
                    </li>
                    {token ? (
                        <>
                            <li onClick={closeMenu}>
                                <Link to="/account" className={getActiveClass("/account")}>
                                    Account
                                </Link>
                            </li>
                            <li>
                                <button
                                    onClick={() => {
                                        handleLogout();
                                        closeMenu();
                                    }}
                                    className="text-gray-700 hover:text-pegasusPurple"
                                >
                                    Ausloggen
                                </button>
                            </li>
                        </>
                    ) : (
                        <li onClick={closeMenu}>
                            <Link to="/login" className="text-gray-700 hover:text-pegasusPurple">
                                Einloggen
                            </Link>
                        </li>
                    )}
                </ul>
            </div>
        </nav>
    );
};

export default Navbar;
